import { arrayOf, bool, node, number, string, shape } from 'prop-types';
import React from 'react';
import css from 'styled-jsx/css';
import RecipeTime from './RecipeTime';
import RecipeIngredients from './RecipeIngredients';
import RecipeSupplies from './RecipeSupplies';
import RecipeInstructions from './RecipeInstructions';
import RecipeNutritionalGuide from './RecipeNutritionalGuide';
import buildRecipeStructuredData from './RecipeStructuredDataFormatter';

// Base recipe used by SiteRecipe, AmpRecipe and FeedRecipe.
// This component should not be opinionated about styles or
// interactions, and needs to work in multiple contexts.

const DEFAULT_STYLES = css``;

function Recipe(props) {
  const {
    title,
    makes,
    serves,
    notes,
    cookTime,
    prepTime,
    imageUrl,
    ingredientGroups,
    instructionGroups,
    supplyGroups,
    nutritionalGuide,
    ingredientsAddOn,
    isAmp,
    showNutrition,
    showJson,
    styles
  } = props;

  return (
    <div className="Recipe">
      {showJson && !!title && !!imageUrl && (
        <script
          className="json-ld-recipe"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildRecipeStructuredData(props))
          }}
        />
      )}

      <h2 className="Recipe__title">{title}</h2>

      <div className="Recipe__supplementalInfo">
        {(makes || serves) && (
          <p className="Recipe__yield">
            <span className="Recipe__yieldLabel">Yield</span>
            {serves ? `Serves ${serves}` : ''}
            {serves && makes && `, `}
            {makes ? `Makes ${makes}` : ''}
          </p>
        )}
        {((cookTime && cookTime.min) || (prepTime && prepTime.min)) && (
          <div className="Recipe__time">
            <RecipeTime cookTime={cookTime} prepTime={prepTime} />
          </div>
        )}
      </div>

      {showNutrition && (
        <div>
          <RecipeNutritionalGuide
            nutritionalGuide={nutritionalGuide}
            isAmp={isAmp}
          />
        </div>
      )}

      {!ingredientGroups || !ingredientGroups.length ? null : (
        <section className="Recipe__ingredientsSection">
          <h3 className="Recipe__ingredientsHeading">Ingredients</h3>
          <RecipeIngredients groups={ingredientGroups} />

          {ingredientsAddOn && (
            <div className="Recipe__ingredientsAddOn">{ingredientsAddOn}</div>
          )}
        </section>
      )}

      {!supplyGroups || !supplyGroups.length ? null : (
        <section className="Recipe__suppliesSection">
          <RecipeSupplies groups={supplyGroups} />
        </section>
      )}

      <h3 className="Recipe__instructionsHeading">Instructions</h3>
      <RecipeInstructions groups={instructionGroups} isAmp={isAmp} />
      {notes && (
        <section className="Recipe__notesSection">
          <h3 className="Recipe__notesHeading">Recipe Notes</h3>
          <div
            className="Recipe__notes"
            dangerouslySetInnerHTML={{ __html: notes }}
          />
        </section>
      )}
      <style jsx>{styles}</style>
    </div>
  );
}

Recipe.defaultProps = {
  title: '',
  makes: '',
  serves: '',
  notes: '',
  cookTime: undefined,
  prepTime: undefined,
  imageUrl: '',
  ingredientGroups: undefined,
  instructionGroups: undefined,
  supplyGroups: undefined,
  nutritionalGuide: undefined,
  ingredientsAddOn: undefined,
  isAmp: false,
  showNutrition: false,
  showJson: true,
  styles: DEFAULT_STYLES
};

Recipe.propTypes = {
  title: string,
  makes: string,
  serves: string,
  notes: string,
  cookTime: shape({
    min: number,
    max: number
  }),
  prepTime: shape({
    min: number,
    max: number
  }),
  imageUrl: string,
  ingredientGroups: arrayOf(
    shape({
      id: string,
      name: string,
      items: arrayOf(
        shape({
          id: string,
          quantity: string,
          unit: string,
          html: string
        })
      )
    })
  ),
  instructionGroups: arrayOf(
    shape({
      id: string,
      name: string,
      items: arrayOf(
        shape({
          id: string,
          html: string,
          imageUrl: string
        })
      )
    })
  ),
  supplyGroups: arrayOf(
    shape({
      id: string,
      name: string,
      items: arrayOf(
        shape({
          id: string,
          quantity: string,
          unit: string,
          html: string,
          affiliateLink: shape({
            url: string
          })
        })
      )
    })
  ),
  nutritionalGuide: shape({
    calories: number,
    labels: arrayOf(string),
    primaryNutrients: arrayOf(
      shape({
        label: string,
        value: string,
        quantity: string,
        schemaValue: string
      })
    ),
    servings: number
  }),

  // Support for any kind of third-party integrations we may be trying
  ingredientsAddOn: node,

  isAmp: bool,

  showNutrition: bool,
  showJson: bool,

  styles: node
};

export default Recipe;
